import React, { useEffect, useState, useRef } from "react";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import UserService from "../../services/user/user.service";

interface Feature {
  featureId: number;
  name: string;
  description?: string | null;
  isActive?: boolean;
}

interface Domain {
  domainId: number;
  domainName: string;
}

interface Role {
  roleID: number;
  name: string;
}

interface DomainFeatures {
  [domainId: number]: Feature[];
}

const FeatureAccessMain: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [allDomainFeatures, setAllDomainFeatures] = useState<DomainFeatures>({});
  const [expandedDomains, setExpandedDomains] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const domainData = await UserService.GetAllDomain();
        setDomains(domainData);

        const rolesData = await UserService.GetAllRoles();
        setRoles(rolesData || []);
      } catch (err) {
        showMessage("Error", "Failed to fetch domain or role data", "error");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  const showMessage = (
    label: string,
    message: string,
    severity: "success" | "info" | "warn" | "error"
  ) => {
    toast.current?.show({
      severity,
      summary: label,
      detail: message,
      life: 3000,
    });
  };

  const toggleDomainExpansion = (domainId: number) => {
    setExpandedDomains(prev =>
      prev.includes(domainId)
        ? prev.filter(id => id !== domainId)
        : [...prev, domainId]
    );
  };

  const handleRoleChange = async (role: Role) => {
    setSelectedRole(role);
    if (!role) return;

    setLoading(true);

    try {
      const roleFeatures = await UserService.GetAllFeatureByRole(role.roleID);
      const featureMap = new Map(roleFeatures.map((f: any) => [f.featureID, f.isActive]));

      const updatedDomainFeatures: DomainFeatures = {};

      for (const domain of domains) {
        const res = await UserService.GetfeatureBYDomainId(domain.domainId);
        const domainFeatures = res.features || [];

        updatedDomainFeatures[domain.domainId] = domainFeatures.map((f: Feature) => ({
          ...f,
          isActive: featureMap.has(f.featureId) ? featureMap.get(f.featureId) : false,
        }));
      }

      setAllDomainFeatures(updatedDomainFeatures);
    } catch (error) {
      console.error("Error loading features by role:", error);
      showMessage("Error", "Failed to load features for selected role", "error");
    } finally {
      setLoading(false);
    }
  };

  const onStatusChange = async (domainId: number, feature: Feature) => {
    if (!selectedRole) {
      showMessage("Warning", "Please select a role first", "warn");
      return;
    }

    const updatedDomainFeatures = { ...allDomainFeatures };
    const updatedFeatures = updatedDomainFeatures[domainId].map((f) =>
      f.featureId === feature.featureId ? { ...f, isActive: !f.isActive } : f
    );
    updatedDomainFeatures[domainId] = updatedFeatures;
    setAllDomainFeatures(updatedDomainFeatures);

    try {
      const userID = parseInt(localStorage.getItem("userID") || "0", 10);

      const payload = {
        roleID: selectedRole.roleID,
        featureID: feature.featureId,
        isActive: !feature.isActive,
        userID: userID,
      };

      const result = await UserService.CreateOrUpdateFeatureMapping(payload);

      if (result?.isSuccess) {
        showMessage("Success", `${feature.name} is now ${payload.isActive ? "Active" : "Inactive"}`, "success");
      } else {
        showMessage("Error", result?.message || "Update failed", "error");
      }
    } catch (err) {
      console.error("Update failed:", err);
      showMessage("Error", "Failed to update feature status", "error");
    }
  };

  const toggleAllFeaturesInDomain = async (domainId: number) => {
    if (!selectedRole) {
      showMessage("Warning", "Please select a role first", "warn");
      return;
    }

    const domainFeatures = allDomainFeatures[domainId];
    if (!domainFeatures || domainFeatures.length === 0) return;

    const allActive = domainFeatures.every(f => f.isActive);
    const newStatus = !allActive;

    const updatedDomainFeatures = { ...allDomainFeatures };
    updatedDomainFeatures[domainId] = domainFeatures.map(f => ({
      ...f,
      isActive: newStatus
    }));
    setAllDomainFeatures(updatedDomainFeatures);

    try {
      const userID = parseInt(localStorage.getItem("userID") || "0", 10);

      const updatePromises = domainFeatures.map(feature =>
        UserService.CreateOrUpdateFeatureMapping({
          roleID: selectedRole.roleID,
          featureID: feature.featureId,
          isActive: newStatus,
          userID: userID,
        })
      );

      const results = await Promise.all(updatePromises);
      const allSuccess = results.every(r => r?.isSuccess);

      if (allSuccess) {
        showMessage("Success", `All features in domain are now ${newStatus ? "Active" : "Inactive"}`, "success");
      } else {
        showMessage("Error", "Some features failed to update", "error");
      }
    } catch (err) {
      console.error("Update failed:", err);
      showMessage("Error", "Failed to update feature status", "error");
    }
  };

  return (
    <div className="flex flex-col min-h-screen font-sans bg-gray-100 p-6">
      <Toast ref={toast} />

      <main className="flex-1">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h1 className="text-2xl font-bold text-gray-800">Feature Access</h1>
            <div className="ml-auto flex items-center gap-3">
              <Dropdown
                value={selectedRole}
                options={roles}
                onChange={(e) => handleRoleChange(e.value)}
                optionLabel="name"
                placeholder="Select Role"
                className="w-64"
              />
            </div>
          </div>

          {loading ? (
            <p className="text-gray-500">Loading...</p>
          ) : domains.length > 0 ? (
            <div className="space-y-4">
              {domains.map((domain) => {
                const domainFeatures = allDomainFeatures[domain.domainId] || [];
                const allFeaturesActive = domainFeatures.length > 0 && domainFeatures.every(f => f.isActive);
                
                return (
                  <div key={domain.domainId} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex justify-between items-center bg-gray-100 p-4 hover:bg-gray-200">
                      <div 
                        className="flex items-center flex-1 cursor-pointer"
                        onClick={() => toggleDomainExpansion(domain.domainId)}
                      >
                        <i className={`pi pi-chevron-${expandedDomains.includes(domain.domainId) ? 'down' : 'right'} mr-3`} />
                        <h2 className="text-lg font-semibold text-gray-700">{domain.domainName}</h2>
                      </div>
                      
                      <div onClick={(e) => e.stopPropagation()}>
                        <InputSwitch
                          checked={allFeaturesActive}
                          onChange={() => toggleAllFeaturesInDomain(domain.domainId)}
                          disabled={domainFeatures.length === 0}
                          tooltip={allFeaturesActive ? "Deselect All" : "Select All"}
                          tooltipOptions={{ position: "left" }}
                        />
                      </div>
                    </div>
                    
                    {expandedDomains.includes(domain.domainId) && (
                      <table className="min-w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-6 py-3">Feature Name</th>
                            <th className="text-left px-6 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {domainFeatures.map((feature) => (
                            <tr key={feature.featureId} className="border-t hover:bg-gray-50">
                              <td className="px-6 py-4">{feature.name}</td>
                              <td className="px-6 py-4">
                                <div className="flex items-center">
                                  <InputSwitch
                                    checked={feature.isActive ?? false}
                                    onChange={() => onStatusChange(domain.domainId, feature)}
                                    tooltip={feature.isActive ? "Active" : "Inactive"}
                                    tooltipOptions={{ position: "top" }}
                                  />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-600">No domains found.</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default FeatureAccessMain;